

import { legacyizeAccessoryNames } from './accessoryNamesLegacy'

const BACKEND_URL = process.env.REACT_APP_API_BASE
export const USE_NEW_API =
    process.env.REACT_APP_USE_NEW_API === '1' ||
    process.env.REACT_APP_USE_NEW_API === 'true'

const json = (url) => fetch(url).then((r) => r.json())

const PAGE_SIZE = 100

const mergeAssets = (into, from = {}) => {
    for (const group of Object.keys(from)) {
        into[group] = { ...(into[group] || {}), ...from[group] }
    }
    return into
}

async function fetchAllTutorials(query = '') {
    const base = `${BACKEND_URL}/api/v2/tutorials?${query ? `${query}&` : ''}limit=${PAGE_SIZE}`
    const data = []
    const assets = {}
    let offset = 0

    while (true) {
        const resp = await json(`${base}&offset=${offset}`)
        const page = resp.data || []
        data.push(...page)
        mergeAssets(assets, resp.assets)

        const total = resp.pagination?.total ?? data.length
        offset += page.length
        if (page.length === 0 || offset >= total) break
    }

    return { data, assets }
}

// ── grouped-assets resolvers (new shape → absolute URL) ──────────────────────
const img = (assets, id) => (id && assets?.images?.[id]) || null
const audio = (assets, id) => (id && assets?.audio?.[id]) || ''
const model = (assets, id) => (id && assets?.models?.[id]) || ''


function adaptMeta(assets, meta = {}) {
    const { audioId, modelId, modelIds, ...rest } = meta
    const out = { ...rest } // keeps animations, title, description, …
    if (audioId) out.audioBaseUrl = audio(assets, audioId)
    if (modelId) out.modelPath = model(assets, modelId)
    else if (Array.isArray(modelIds) && modelIds.length)
        out.modelPath = modelIds.map((id) => model(assets, id)).filter(Boolean)
    return out
}


function adaptTutorialFull(t, assets) {
    return {
        id: t.id,
        title: t.title,
        description: t.description,
        image: img(assets, t.imageId),
        kit: t.kit || null,
        playScreenId: t.playScreenId || null,
        link: t.link || null,
        screens: (t.screens || []).map((s) => ({
            id: s.id,
            type: s.type,
            hidden: !!s.hidden,
            projectId: t.id,
            
            __runtimeData: {
                ...legacyizeAccessoryNames(s.content || {}),
                meta: adaptMeta(assets, s.meta),
            },
        })),
    }
}

/** A summary tutorial → light OLD subprogram card ({id,title,image,kit,screens[{type}]}). */
function adaptTutorialSummary(t, assets) {
    return {
        id: t.id,
        title: t.title,
        image: img(assets, t.imageId),
        kit: t.kit || null,
        playScreenId: t.playScreenId || null,
        screens: (t.screenTypes || []).map((type) => ({ type })),
        screenCount: t.screenCount,
        screenTypes: t.screenTypes,
    }
}

// ── ① change detection ───────────────────────────────────────────────────────
export const getContentVersion = () =>
    json(`${BACKEND_URL}/api/v2/content-version`)

// ── ② catalog (kit-selection) ────────────────────────────────────────────────
/** OLD /api/expansion-kits shape: [{ id, title, image, available, devices }]. */
export async function getExpansionKits() {
    const resp = await json(`${BACKEND_URL}/api/v2/kit-selection`)
    return (resp.kits || []).map((k) => ({
        id: k.id,
        title: k.title,
        image: img(resp.assets, k.imageId),
        available: k.available,
        // Per-device availability — used in the device-first kit selection view
        // so a kit can be "Coming Soon" for one device but available for another.
        devices: (k.devices || []).map((d) => ({
            id: d.id,
            name: d.name,
            available: d.available,
        })),
    }))
}

/**
 * Expansion-kit → device → tutorials, pre-filtered to that kit+device.
 * OLD shape: { subprograms: [{ id, title, image, kit, screens:[{type}] }] }.
 * Replaces `GET /api/program/:device` + the client-side `sub.kit === kitId` filter.
 */
export async function getKitDeviceSubprograms(kitId, deviceId) {
    const q = kitId
        ? `kit=${encodeURIComponent(kitId)}&device=${encodeURIComponent(deviceId)}`
        : `device=${encodeURIComponent(deviceId)}`
    const resp = await fetchAllTutorials(`${q}&fields=summary`)
    return {
        subprograms: (resp.data || []).map((t) => ({
            ...adaptTutorialSummary(t, resp.assets),
            kit: t.kit || kitId || null,
        })),
    }
}

// ── ③ tutorials ──────────────────────────────────────────────────────────────
/** OLD /api/program/:device shape (light list): { subprograms:[{id,title,image,kit,screens[{type}]}] }. */
export async function getProgramSubprograms(deviceId) {
    const resp = await fetchAllTutorials(
        `device=${encodeURIComponent(deviceId)}&fields=summary`
    )
    return {
        subprograms: (resp.data || []).map((t) =>
            adaptTutorialSummary(t, resp.assets)
        ),
    }
}

/**
 * OLD /api/program/:device shape but FULL (subprograms + screens + pre-loaded
 * runtime content). Used by ContentEngine so navigation needs no per-screen fetch.
 */
export async function getProgramFull(deviceId) {
    const resp = await fetchAllTutorials(`device=${encodeURIComponent(deviceId)}`)
    return {
        subprograms: (resp.data || []).map((t) =>
            adaptTutorialFull(t, resp.assets)
        ),
    }
}


export async function getSubprogramDetail(subId) {
    const resp = await json(
        `${BACKEND_URL}/api/v2/tutorials?id=${encodeURIComponent(subId)}`
    )
    return { subprograms: (resp.data || []).map((t) => adaptTutorialFull(t, resp.assets)) }
}


export async function getCodableDevices() {
    const resp = await json(`${BACKEND_URL}/api/v2/kit-selection`)
    const byId = new Map()
    for (const k of resp.kits || []) {
        for (const d of k.devices || []) {
            const has = (d.tutorials && d.tutorials.length > 0) || d.available
            if (has && !byId.has(d.id)) byId.set(d.id, { id: d.id, name: d.name })
        }
    }
    return [...byId.values()]
}

/** Flat list of every tutorial for the search index (KitSelection). */
export async function getAllTutorialsSummary() {
    const resp = await fetchAllTutorials('fields=summary')
    return (resp.data || []).map((t) => ({
        id: t.id,
        device: t.device,
        title: t.title,
        image: img(resp.assets, t.imageId),
        kit: t.kit || null,
    }))
}

const contentApi = {
    USE_NEW_API,
    getContentVersion,
    getExpansionKits,
    getKitDeviceSubprograms,
    getProgramSubprograms,
    getProgramFull,
    getSubprogramDetail,
    getCodableDevices,
    getAllTutorialsSummary,
}

export default contentApi
